import React, { useEffect, useMemo, useRef, useState } from 'react'
import type {
  DocumentationFileIds,
  DocumentationSlots,
  DocumentationSlotSources,
  DraftMetadataFileEntry
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  buildDocumentationDisplayRows,
  DOCUMENTATION_SLOT_DEFINITIONS,
  DOCUMENTATION_SLOT_FILE_ACCEPT,
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotSourcesHaveData,
  draftMetadataFileLabel,
  isFullPageSelection,
  normalizePageList
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  formatSlotOriginSummary,
  type DocumentationSlotKey as SummarySlotKey
} from '../../../../../utils/conversationalMode/documentationSlotSummaryUtils.ts'
import styles from './ProjectAutofillCard.module.scss'
import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'

const parsePageSegments = (raw: string, maxPage: number): number[] => {
  const out = new Set<number>()
  const parts = raw
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean)
  for (const part of parts) {
    const range = part.match(/^(\d+)\s*-\s*(\d+)$/)
    if (range) {
      const a = Number(range[1])
      const b = Number(range[2])
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue
      const lo = Math.min(Math.floor(a), Math.floor(b))
      const hi = Math.max(Math.floor(a), Math.floor(b))
      for (let p = lo; p <= hi; p++) {
        if (p >= 1 && p <= maxPage) out.add(p)
      }
    } else {
      const n = Number(part)
      if (!Number.isFinite(n)) continue
      const p = Math.floor(n)
      if (p >= 1 && p <= maxPage) out.add(p)
    }
  }
  return Array.from(out).sort((a, b) => a - b)
}

const summarizePagesShort = (pages: number[] | undefined, maxItems = 12): string => {
  const norm = normalizePageList(pages)
  if (!norm?.length) return ''
  if (norm.length <= maxItems) return norm.join(', ')
  return `${norm.slice(0, maxItems).join(', ')}… (+${norm.length - maxItems})`
}

type SlotKey = (typeof DOCUMENTATION_SLOT_KEYS)[number]

type SlotFileTriggerProps = {
  disabled: boolean
  isUploading: boolean
  onFile: (file: File) => void
}

const RubroDocSlotFileTrigger: React.FC<SlotFileTriggerProps> = ({ disabled, isUploading, onFile }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        className={styles.projectAutofillRubroDocUploadBtn}
        disabled={disabled || isUploading}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          inputRef.current?.click()
        }}
      >
        {isUploading ? 'Subiendo…' : 'Subir'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={DOCUMENTATION_SLOT_FILE_ACCEPT}
        disabled={disabled || isUploading}
        className={styles.projectAutofillRubroDocFileInput}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) onFile(f)
        }}
      />
    </>
  )
}

type PagePickerProps = {
  maxPage: number
  value: number[] | undefined
  pdfBaseUrl?: string
  onChange: (pages: number[]) => void
  showSelectedChips?: boolean
}

const DocumentationSlotPagePicker: React.FC<PagePickerProps> = ({
  maxPage,
  value,
  pdfBaseUrl,
  onChange,
  showSelectedChips = true
}) => {
  const selected = useMemo(() => new Set(normalizePageList(value) ?? []), [value])
  const sorted = useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected])
  const allPages = useMemo(() => Array.from({ length: maxPage }, (_, i) => i + 1), [maxPage])
  const [addText, setAddText] = useState('')
  const basePdf = pdfBaseUrl?.trim() || ''

  const applyAddText = () => {
    const parsed = parsePageSegments(addText, maxPage)
    if (!parsed.length) return
    const next = new Set(selected)
    for (const p of parsed) next.add(p)
    onChange(Array.from(next).sort((a, b) => a - b))
    setAddText('')
  }

  const remove = (p: number) => {
    const next = new Set(selected)
    next.delete(p)
    onChange(Array.from(next).sort((a, b) => a - b))
  }

  return (
    <div className={styles.projectAutofillRubroDocPagePicker}>
      {showSelectedChips ? (
        <div className={styles.projectAutofillRubroDocPagePickerChips} aria-label="Páginas elegidas">
          {sorted.length === 0 ? (
            <span className={styles.projectAutofillRubroDocPagePickerEmpty}>Todavía no elegiste páginas.</span>
          ) : (
            sorted.map((p) => (
              <span key={p} className={styles.projectAutofillRubroDocPagePickerChip}>
                {basePdf ? (
                  <a
                    href={`${basePdf}#page=${p}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.projectAutofillRubroDocPagePickerChipLink}
                  >
                    Página{' '}
                    <span className={styles.projectAutofillRubroDocPagePickerChipNum}>{p}</span>
                  </a>
                ) : null}
                <button
                  type="button"
                  className={styles.projectAutofillRubroDocPagePickerChipRemove}
                  onClick={() => remove(p)}
                  aria-label={`Quitar página ${p}`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
      ) : (
        <ProjectAutofillInfoNote variant="rubroInfo">
          Las páginas que agregues para este tipo aparecen en el listado superior con su etiqueta.
        </ProjectAutofillInfoNote>
      )}

      <div className={styles.projectAutofillRubroDocPagePickerAddRow}>
        <input
          type="text"
          className={styles.projectAutofillRubroDocPagePickerInput}
          value={addText}
          onChange={(e) => setAddText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              applyAddText()
            }
          }}
          placeholder={`Páginas del 1 al ${maxPage} (ej. 2, 5, 10-14)`}
        />
        <button type="button" className={styles.projectAutofillRubroDocPagePickerAddBtn} onClick={applyAddText}>
          Agregar
        </button>
      </div>

      <div className={styles.projectAutofillRubroDocPagePickerQuick}>
        <button type="button" className={styles.projectAutofillRubroDocPagePickerQuickBtn} onClick={() => onChange([...allPages])}>
          Elegir todas ({maxPage})
        </button>
        <span className={styles.projectAutofillRubroDocPagePickerQuickSep}>·</span>
        <button type="button" className={styles.projectAutofillRubroDocPagePickerQuickBtn} onClick={() => onChange([])}>
          Quitar todas
        </button>
      </div>
    </div>
  )
}

type Props = {
  tempId: string
  slots: DocumentationSlots
  pdfBaseUrl?: string
  maxPage?: number
  fileIds: DocumentationFileIds
  draftFiles: DraftMetadataFileEntry[]
  disabled?: boolean
  title?: string
  uploadingSlotKey?: string | null
  onSelectSlotFileId: (slot: SlotKey, fileId: string | null) => void
  onSlotPagesChange?: (slot: SlotKey, pages: number[]) => void
  onRemoveSlotPageChip?: (slot: SlotKey, page: number, fileId?: string) => void
  documentationSlotSources?: DocumentationSlotSources
  onUploadSlotFile: (slot: SlotKey, file: File) => Promise<void>
  expandSlotKey?: SummarySlotKey | null
  onExpandSlotConsumed?: () => void
  correctionMode?: boolean
  correctionSlot?: SummarySlotKey
}

const RubroDocumentationBlock: React.FC<Props> = ({
  tempId,
  slots,
  pdfBaseUrl,
  maxPage,
  fileIds,
  draftFiles,
  disabled,
  title,
  uploadingSlotKey,
  onSelectSlotFileId,
  onSlotPagesChange,
  onRemoveSlotPageChip,
  documentationSlotSources,
  onUploadSlotFile,
  expandSlotKey,
  onExpandSlotConsumed,
  correctionMode = false,
  correctionSlot
}) => {
  const resolvedMaxPage =
    typeof maxPage === 'number' && Number.isFinite(maxPage) && maxPage >= 1 ? maxPage : null
  const canPickPages =
    resolvedMaxPage != null && typeof onSlotPagesChange === 'function' && !disabled

  const [subsetChosen, setSubsetChosen] = useState<Partial<Record<SlotKey, boolean>>>({})
  const [fileChosen, setFileChosen] = useState<Partial<Record<SlotKey, boolean>>>({})
  const [reconfigureOpen, setReconfigureOpen] = useState<Partial<Record<SlotKey, boolean>>>({})
  const legajoRootRef = useRef<HTMLDivElement>(null)
  const advancedSlotsRef = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    if (!expandSlotKey) return
    setReconfigureOpen((prev) => ({ ...prev, [expandSlotKey]: true }))
    if (
      expandSlotKey === 'metricComputation' ||
      expandSlotKey === 'budget' ||
      expandSlotKey === 'technicalSpecifications'
    ) {
      if (advancedSlotsRef.current) advancedSlotsRef.current.open = true
    }
    const t = window.setTimeout(() => {
      legajoRootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      onExpandSlotConsumed?.()
    }, 80)
    return () => window.clearTimeout(t)
  }, [expandSlotKey, onExpandSlotConsumed])

  const applySlotMode = (slot: SlotKey, mode: 'full' | 'subset' | 'file') => {
    if (!onSlotPagesChange || !onSelectSlotFileId) return
    if (mode === 'file') {
      onSlotPagesChange(slot, [])
      setSubsetChosen((prev) => ({ ...prev, [slot]: false }))
      setFileChosen((prev) => ({ ...prev, [slot]: true }))
      return
    }
    setFileChosen((prev) => ({ ...prev, [slot]: false }))
    onSelectSlotFileId(slot, null)
    if (mode === 'full' && resolvedMaxPage) {
      onSlotPagesChange(slot, Array.from({ length: resolvedMaxPage }, (_, i) => i + 1))
      setSubsetChosen((prev) => ({ ...prev, [slot]: false }))
      return
    }
    if (mode === 'subset') {
      setSubsetChosen((prev) => ({ ...prev, [slot]: true }))
    }
  }

  const deriveSlotMode = (
    slot: SlotKey,
    pages: number[] | undefined,
    hasFile: boolean,
    hasPages: boolean
  ) => {
    if (hasFile) return 'file'
    if (fileChosen[slot]) return 'file'
    if (!resolvedMaxPage) return hasPages ? 'subset' : 'empty'
    if (isFullPageSelection(pages, resolvedMaxPage)) return 'full'
    if (hasPages || subsetChosen[slot]) return 'subset'
    return 'empty'
  }

  const basePdf = pdfBaseUrl?.trim() || ''

  const displayRows = useMemo(
    () => buildDocumentationDisplayRows(slots, fileIds, documentationSlotSources),
    [slots, fileIds, documentationSlotSources]
  )

  const removeTypeFromPage = (slot: SlotKey, pageNum: number, rowFileId?: string) => {
    if (onRemoveSlotPageChip) {
      onRemoveSlotPageChip(slot, pageNum, rowFileId?.trim() ? rowFileId.trim() : undefined)
      return
    }
    if (!onSlotPagesChange) return
    const cur = normalizePageList(slots[slot]) ?? []
    onSlotPagesChange(
      slot,
      cur.filter((p) => p !== pageNum)
    )
  }

  const canEditPageChips =
    (typeof onRemoveSlotPageChip === 'function' || typeof onSlotPagesChange === 'function') && !disabled

  const slotMetaList = useMemo(
    () =>
      DOCUMENTATION_SLOT_DEFINITIONS.map(({ key, label }) => {
        const pages = slots[key]
        const hasPages = Array.isArray(pages) && pages.length > 0
        const fid = fileIds[key]
        const hasFile = typeof fid === 'string' && fid.trim().length > 0
        const resolved = hasFile || hasPages
        return { key, label, pages, hasPages, fid, hasFile, resolved }
      }),
    [slots, fileIds]
  )

  const auxiliarySlotKeys: SlotKey[] = ['planos', 'technicalSpecifications']

  const renderFileControls = (key: SlotKey, isUploading: boolean) => (
    <div
      className={styles.projectAutofillRubroDocSlotActions}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <select
        className={styles.projectAutofillRubroDocSelect}
        value={fileIds[key] ? String(fileIds[key]) : ''}
        disabled={disabled || isUploading}
        onChange={(e) => {
          const v = e.target.value.trim()
          onSelectSlotFileId(key, v.length ? v : null)
        }}
      >
        <option value="">Archivo del borrador…</option>
        {draftFiles.map((f) => {
          const id = f.id != null ? String(f.id) : ''
          if (!id) return null
          return (
            <option key={id} value={id}>
              {draftMetadataFileLabel(f)}
            </option>
          )
        })}
      </select>
      <RubroDocSlotFileTrigger
        disabled={!!disabled}
        isUploading={isUploading}
        onFile={(file) => void onUploadSlotFile(key, file)}
      />
    </div>
  )

  const fullLabel =
    resolvedMaxPage != null
      ? `PDF del pliego completo (${resolvedMaxPage} ${resolvedMaxPage === 1 ? 'página' : 'páginas'})`
      : 'PDF del pliego completo'

  const slotSummaryText = (key: SlotKey) =>
    formatSlotOriginSummary({
      slot: key,
      slots,
      fileIds,
      draftFiles,
      maxPage: resolvedMaxPage,
      pdfBaseUrl
    }).text

  const renderSlotSourcesLines = (slot: SlotKey) => {
    const entries = documentationSlotSources?.[slot]
    if (!entries?.length) return null
    return (
      <ul className={styles.projectAutofillRubroDocSlotSourcesList}>
        {entries.map((ent, idx) => {
          const file = draftFiles.find((x) => String(x.id ?? '') === String(ent.fileId))
          const name = file ? draftMetadataFileLabel(file) : `${String(ent.fileId).slice(0, 8)}…`
          const pagesTxt = summarizePagesShort(ent.pages) || '—'
          return (
            <li key={`${ent.fileId}-${idx}`} className={styles.projectAutofillRubroDocSlotSourcesItem}>
              <span className={styles.projectAutofillRubroDocSlotSourcesName}>{name}</span>
              <span className={styles.projectAutofillRubroDocSlotSourcesPages}> · págs. {pagesTxt}</span>
            </li>
          )
        })}
      </ul>
    )
  }

  const renderSlotEditorContent = (meta: (typeof slotMetaList)[0]) => {
    const { key, pages, hasPages, fid, hasFile } = meta
    const entry = hasFile ? draftFiles.find((f) => String(f.id ?? '') === String(fid)) : undefined
    const uploadKey = `${tempId}:${key}`
    const isUploading = uploadingSlotKey === uploadKey
    const slotMode = deriveSlotMode(key, pages, hasFile, hasPages)
    const selectId = `doc-source-${tempId}-${key}`
    const fileControls = renderFileControls(key, isUploading)

    return (
      <>
        {canPickPages ? (
          <>
            <label className={styles.projectAutofillRubroDocSourceLabel} htmlFor={selectId}>
              ¿Qué querés enviar?
            </label>
            <select
              id={selectId}
              className={styles.projectAutofillRubroDocSourceSelect}
              value={slotMode === 'empty' ? '' : slotMode}
              disabled={disabled || isUploading}
              onChange={(e) => {
                const v = e.target.value
                if (v === 'full' || v === 'subset' || v === 'file') applySlotMode(key, v)
              }}
            >
              <option value="">Seleccioná…</option>
              <option value="full">{fullLabel}</option>
              <option value="subset">Solo algunas hojas del PDF</option>
              <option value="file">Otro archivo (no el PDF del pliego)</option>
            </select>

            {slotMode === 'full' && resolvedMaxPage ? (
              <ProjectAutofillInfoNote variant="rubroInfo">
                Se incluye el documento analizado entero.
                {basePdf ? (
                  <>
                    {' '}
                    <a href={basePdf} target="_blank" rel="noopener noreferrer" className={styles.projectAutofillRubroDocPageLink}>
                      Ver PDF
                    </a>
                  </>
                ) : null}
              </ProjectAutofillInfoNote>
            ) : null}

            {slotMode === 'subset' && resolvedMaxPage ? (
              <DocumentationSlotPagePicker
                maxPage={resolvedMaxPage}
                value={pages}
                pdfBaseUrl={pdfBaseUrl}
                showSelectedChips={false}
                onChange={(next) => onSlotPagesChange!(key, next)}
              />
            ) : null}

            {slotMode === 'file' ? (
              <>
                <p className={styles.projectAutofillRubroDocAttachedLine}>Documento adjunto (no es el PDF del pliego recortado).</p>
                {hasFile ? (
                  <div className={styles.projectAutofillRubroDocFilePicked}>
                    {entry?.storageUrl?.trim() ? (
                      <a
                        href={entry.storageUrl.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.projectAutofillRubroDocPageLink}
                      >
                        {draftMetadataFileLabel(entry)}
                      </a>
                    ) : (
                      <span className={styles.projectAutofillRubroDocFileName}>{entry ? draftMetadataFileLabel(entry) : fid}</span>
                    )}
                    <button
                      type="button"
                      className={styles.projectAutofillRubroDocClear}
                      disabled={disabled || isUploading}
                      onClick={() => onSelectSlotFileId(key, null)}
                    >
                      Quitar
                    </button>
                  </div>
                ) : null}
                {fileControls}
              </>
            ) : null}
          </>
        ) : hasFile ? (
          <>
            <p className={styles.projectAutofillRubroDocAttachedLine}>Documento adjunto.</p>
            <div className={styles.projectAutofillRubroDocFilePicked}>
              {entry?.storageUrl?.trim() ? (
                <a
                  href={entry.storageUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.projectAutofillRubroDocPageLink}
                >
                  {draftMetadataFileLabel(entry)}
                </a>
              ) : (
                <span className={styles.projectAutofillRubroDocFileName}>{entry ? draftMetadataFileLabel(entry) : fid}</span>
              )}
              <button
                type="button"
                className={styles.projectAutofillRubroDocClear}
                disabled={disabled || isUploading}
                onClick={() => onSelectSlotFileId(key, null)}
              >
                Quitar
              </button>
            </div>
            {fileControls}
          </>
        ) : hasPages ? (
          <>
            <ProjectAutofillInfoNote variant="rubroInfo">
              {displayRows.length > 0 ? 'Las hojas de este tipo están en el listado superior. ' : ''}
              No podemos ajustar hojas sin el total de páginas del PDF. Podés adjuntar otro archivo.
            </ProjectAutofillInfoNote>
            {fileControls}
          </>
        ) : (
          <>
            <ProjectAutofillInfoNote variant="rubroInfo">Sin documentación para este tipo.</ProjectAutofillInfoNote>
            {fileControls}
          </>
        )}
      </>
    )
  }

  const slotsForLegajoList = slotMetaList.filter(
    (m) => auxiliarySlotKeys.includes(m.key) || !m.resolved
  )
  const slotsForEditorList =
    slotsForLegajoList.length > 0 ? slotsForLegajoList : slotMetaList.filter((m) => auxiliarySlotKeys.includes(m.key))

  if (correctionMode) {
    const slotsToEdit = correctionSlot
      ? slotMetaList.filter((m) => m.key === correctionSlot)
      : slotMetaList
    return (
      <div
        ref={legajoRootRef}
        id={`rubro-legajo-${tempId}${correctionSlot ? `-${correctionSlot}` : ''}`}
        className={styles.projectAutofillRubroDoc}
        title={title}
      >
        <ul className={styles.projectAutofillRubroDocCompactList}>
          {slotsToEdit.map((meta) => (
            <li key={meta.key} className={styles.projectAutofillRubroDocCompactAccordionItem}>
              <div
                className={`${styles.projectAutofillRubroDocCompactAccordionInner} ${styles.projectAutofillRubroDocCompactAccordionInnerOpen}`}
              >
                <div className={styles.projectAutofillRubroDocCompactRow}>
                  <span className={styles.projectAutofillRubroDocCompactKind}>{meta.label}</span>
                  <span className={styles.projectAutofillRubroDocCompactSummary}>{slotSummaryText(meta.key)}</span>
                </div>
                <div className={styles.projectAutofillRubroDocAccordionPanel} role="region">
                  {renderSlotEditorContent(meta)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderSlotRow = (meta: (typeof slotMetaList)[0], emphasizeAuxiliary: boolean) => {
    const panelId = `rubro-legajo-slot-${tempId}-${meta.key}`
    const expanded = !!reconfigureOpen[meta.key]
    const showEditor = expanded || (!meta.resolved && emphasizeAuxiliary)
    const summaryLine = slotSummaryText(meta.key)
    const hideWhenInTabs =
      meta.key === 'metricComputation' || meta.key === 'budget' || meta.key === 'technicalSpecifications'

    return (
      <li
        key={meta.key}
        id={panelId}
        className={styles.projectAutofillRubroDocCompactAccordionItem}
      >
        <div
          className={
            expanded
              ? `${styles.projectAutofillRubroDocCompactAccordionInner} ${styles.projectAutofillRubroDocCompactAccordionInnerOpen}`
              : styles.projectAutofillRubroDocCompactAccordionInner
          }
        >
          <div className={styles.projectAutofillRubroDocCompactRow}>
            <span className={styles.projectAutofillRubroDocCompactKind}>{meta.label}</span>
            <span className={styles.projectAutofillRubroDocCompactSummary}>{summaryLine}</span>
            {canPickPages ? (
              <span className={styles.projectAutofillRubroDocFileRow}>
                {meta.hasFile ? (
                  <button
                    type="button"
                    className={styles.projectAutofillRubroDocTextBtn}
                    disabled={!!disabled}
                    onClick={() => onSelectSlotFileId(meta.key, null)}
                  >
                    Quitar archivo
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.projectAutofillRubroDocTextBtn}
                  disabled={!!disabled}
                  aria-expanded={expanded}
                  aria-controls={`${panelId}-panel`}
                  onClick={() =>
                    setReconfigureOpen((prev) => {
                      const next = { ...prev }
                      if (next[meta.key]) delete next[meta.key]
                      else next[meta.key] = true
                      return next
                    })
                  }
                >
                  {expanded ? 'Ocultar' : meta.resolved ? 'Cambiar origen' : 'Configurar'}
                </button>
              </span>
            ) : null}
          </div>
          {hideWhenInTabs && meta.resolved ? (
            <ProjectAutofillInfoNote variant="rubroInfo">
              El origen de {meta.label.toLowerCase()} se ajusta desde las pestañas de borradores arriba.
            </ProjectAutofillInfoNote>
          ) : null}
          {renderSlotSourcesLines(meta.key)}
          {showEditor && !(hideWhenInTabs && meta.resolved && !expanded) ? (
            <div
              id={`${panelId}-panel`}
              className={styles.projectAutofillRubroDocAccordionPanel}
              role="region"
              aria-label={`Origen: ${meta.label}`}
            >
              {renderSlotEditorContent(meta)}
            </div>
          ) : null}
        </div>
      </li>
    )
  }

  return (
    <div
      ref={legajoRootRef}
      id={`rubro-legajo-${tempId}`}
      className={styles.projectAutofillRubroDoc}
      title={title}
    >
      <span className={styles.projectAutofillRubroDocTitle}>Legajo del rubro</span>
      <p className={styles.projectAutofillRubroDocNote}>
        Planos y el detalle página por página del análisis. Especificaciones técnicas, cómputo y cotización editables están en las pestañas
        de borradores arriba.
      </p>

      {slotsForEditorList.length > 0 ? (
        <ul className={styles.projectAutofillRubroDocCompactList}>
          {slotsForEditorList.map((meta) => renderSlotRow(meta, true))}
        </ul>
      ) : (
        <ul className={styles.projectAutofillRubroDocCompactList}>
          {slotMetaList
            .filter((m) => auxiliarySlotKeys.includes(m.key))
            .map((meta) => renderSlotRow(meta, false))}
        </ul>
      )}

      {slotMetaList.some(
        (m) =>
          (m.key === 'metricComputation' || m.key === 'budget' || m.key === 'technicalSpecifications') &&
          m.resolved
      ) ? (
        <details ref={advancedSlotsRef} className={styles.projectAutofillRubroDocAdvancedSlots}>
          <summary className={styles.projectAutofillRubroDocAdvancedSummary}>
            Ajustar origen en el legajo (especificaciones, cómputo o presupuesto)
          </summary>
          <ul className={styles.projectAutofillRubroDocCompactList}>
            {slotMetaList
              .filter(
                (m) =>
                  m.key === 'metricComputation' || m.key === 'budget' || m.key === 'technicalSpecifications'
              )
              .map((meta) => renderSlotRow(meta, false))}
          </ul>
        </details>
      ) : null}

      {displayRows.length > 0 ? (
        <details className={styles.projectAutofillRubroDocPageDetails}>
          <summary className={styles.projectAutofillRubroDocPageDetailsSummary}>
            Páginas detectadas ({displayRows.length})
          </summary>
          <div className={styles.projectAutofillRubroDocStack}>
            <span className={styles.projectAutofillRubroDocByPageHeading}>
              {documentationSlotSourcesHaveData(documentationSlotSources ?? {})
                ? 'Páginas por archivo analizado'
                : 'Páginas del archivo analizado'}
            </span>
            <ul className={styles.projectAutofillRubroDocByPageList}>
              {displayRows.map(({ fileId, page, types }) => {
                const fid = String(fileId || '').trim()
                const fileEntry = fid ? draftFiles.find((f) => String(f.id ?? '') === fid) : undefined
                const rowPdf = fid
                  ? String(fileEntry?.storageUrl || '').trim()
                  : basePdf
                const rowKey = fid ? `${fid}:${page}` : `legacy:${page}`
                const pageLabel =
                  fid && fileEntry
                    ? `${draftMetadataFileLabel(fileEntry)} · página ${page}`
                    : fid
                      ? `${fid.slice(0, 8)}… · página ${page}`
                      : `Página ${page}`
                return (
                  <li key={rowKey} className={styles.projectAutofillRubroDocByPageRow}>
                    <span className={styles.projectAutofillRubroDocByPageNum}>
                      {rowPdf ? (
                        <a
                          href={`${rowPdf.split('#')[0]}#page=${page}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.projectAutofillRubroDocPageLink}
                        >
                          {pageLabel}
                        </a>
                      ) : (
                        pageLabel
                      )}
                    </span>
                    <span className={styles.projectAutofillRubroDocByPageChips}>
                      {types.map((slotKey) => {
                        const def = DOCUMENTATION_SLOT_DEFINITIONS.find((d) => d.key === slotKey)
                        const chipText = def?.label ?? String(slotKey)
                        return (
                          <span key={slotKey} className={styles.projectAutofillRubroDocSlotTypeChip}>
                            {chipText}
                            {canEditPageChips ? (
                              <button
                                type="button"
                                className={styles.projectAutofillRubroDocSlotTypeChipRemove}
                                disabled={disabled}
                                onClick={() => removeTypeFromPage(slotKey, page, fid || undefined)}
                                aria-label={`Quitar ${chipText} de la página ${page}`}
                              >
                                ×
                              </button>
                            ) : null}
                          </span>
                        )
                      })}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </details>
      ) : null}
    </div>
  )
}

export default RubroDocumentationBlock
